<script setup lang="ts">
  import CalendarEventComposerModal from '@/components/app/calendar/CalendarEventComposerModal.vue'
  import CalendarEventDetailDialog from '@/components/app/calendar/CalendarEventDetailDialog.vue'
  import CalendarSettingsModal from '@/components/app/calendar/CalendarSettingsModal.vue'
  import CalendarMainEmptyState from '@/components/app/calendar/CalendarMainEmptyState.vue'
  import CalendarMainPanel from '@/components/app/calendar/CalendarMainPanel.vue'
  import CalendarMainToolbar from '@/components/app/calendar/CalendarMainToolbar.vue'
  import CalendarSidebar from '@/components/app/calendar/CalendarSidebar.vue'
  import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { APPOINTMENTS_CALENDAR_SETUP_PATH } from '@/constants/calendar/external-links'
  import { CALENDAR_SIDEBAR_WIDTH } from '@/constants/calendar/layout'
  import { useCalendarKindChecks } from '@/composables/useCalendarKindChecks'
  import { useCalendarPage, type CalendarPageFilterLabels } from '@/composables/useCalendarPage'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast } from '@/lib/toasts'
  import { ROUTES } from '@/router/routes'
  import { useUserStore } from '@/store/userStore'
  import type { CalendarGridEvent } from '@/types/calendar/grid'
  import {
    CalendarEventTypeEnum,
    CalendarEventsService,
    AccountsService,
    type CalendarEventRequest
  } from '@/types/generated'
  import { ApiError } from '@/types/generated/core/ApiError'
  import {
    calendarAuthErrorTranslations,
    getCalendarAuthErrorMessage
  } from '@/utils/calendar/auth-errors'
  import type { CalendarEventTypeLabels } from '@/utils/calendar/event-display'
  import { composerModeForEvent } from '@/utils/calendar/event-types'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const { t } = useTranslation({
    en: {
      mon: 'Mo',
      tue: 'Tu',
      wed: 'We',
      thu: 'Th',
      fri: 'Fr',
      sat: 'Sa',
      sun: 'Su',
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      calendars: 'Calendars',
      settings: 'Settings',
      expandFilters: 'Show filters',
      collapseFilters: 'Hide filters',
      hideSidebar: 'Hide calendar sidebar',
      showSidebar: 'Show calendar sidebar',
      viewDay: 'Day',
      viewWeek: 'Week',
      viewMonth: 'Month',
      viewYear: 'Year',
      prevDay: 'Previous day',
      prevWeek: 'Previous week',
      prevMonthNav: 'Previous month',
      prevYear: 'Previous year',
      nextDay: 'Next day',
      nextWeek: 'Next week',
      nextMonthNav: 'Next month',
      nextYear: 'Next year',
      today: 'Today',
      allDay: 'All day',
      more: 'more',
      addEvent: 'Add event',
      addTermin: 'Appointment',
      addFrist: 'Deadline',
      addWiedervorlage: 'Reschedule',
      addAufgabe: 'Task',
      eventTypeGerichtstermin: 'Court hearing',
      eventTypePrivate: 'Private',
      filterGeneralAppointments: 'Appointments',
      filterCourtAppointments: 'Court hearings',
      filterDeadlines: 'Deadlines',
      filterResubmissions: 'Resubmissions',
      filterTasks: 'Tasks',
      filterPrivate: 'Private',
      emptyTitle: 'No calendar connected',
      emptyDescription: 'Connect an external calendar to see your appointments here.',
      setupCta: 'Set up now',
      loadFailed: 'Could not load calendars.',
      retry: 'Try again',
      ...calendarAuthErrorTranslations.en,
      moveEventTitle: 'Move event?',
      moveEventDescription: 'Do you want to move this event to the new time?',
      moveEventNewTime: 'New time',
      deleteEventTitle: 'Delete event?',
      deleteEventDescription: 'Do you want to delete this event?',
      cancel: 'Cancel',
      confirmMove: 'Move',
      confirmDelete: 'Delete'
    },
    de: {
      mon: 'Mo',
      tue: 'Di',
      wed: 'Mi',
      thu: 'Do',
      fri: 'Fr',
      sat: 'Sa',
      sun: 'So',
      prevMonth: 'Vorheriger Monat',
      nextMonth: 'Nächster Monat',
      calendars: 'Kalender',
      settings: 'Einstellungen',
      expandFilters: 'Filter anzeigen',
      collapseFilters: 'Filter ausblenden',
      hideSidebar: 'Kalender-Seitenleiste ausblenden',
      showSidebar: 'Kalender-Seitenleiste einblenden',
      viewDay: 'Tag',
      viewWeek: 'Woche',
      viewMonth: 'Monat',
      viewYear: 'Jahr',
      prevDay: 'Vorheriger Tag',
      prevWeek: 'Vorherige Woche',
      prevMonthNav: 'Vorheriger Monat',
      prevYear: 'Vorheriges Jahr',
      nextDay: 'Nächster Tag',
      nextWeek: 'Nächste Woche',
      nextMonthNav: 'Nächster Monat',
      nextYear: 'Nächstes Jahr',
      today: 'Heute',
      allDay: 'Ganztägig',
      more: 'weitere',
      addEvent: 'Termin hinzufügen',
      addTermin: 'Termin',
      addFrist: 'Frist',
      addWiedervorlage: 'Wiedervorlage',
      addAufgabe: 'Aufgabe',
      eventTypeGerichtstermin: 'Gerichtstermin',
      eventTypePrivate: 'Privat',
      filterGeneralAppointments: 'Termine (allgemein)',
      filterCourtAppointments: 'Gerichtstermine',
      filterDeadlines: 'Fristen',
      filterResubmissions: 'Wiedervorlagen',
      filterTasks: 'Aufgaben',
      filterPrivate: 'Privat',
      emptyTitle: 'Kein Kalender verbunden',
      emptyDescription: 'Verbinden Sie einen externen Kalender, um Ihre Termine hier zu sehen.',
      setupCta: 'Jetzt einrichten',
      loadFailed: 'Kalender konnten nicht geladen werden.',
      retry: 'Erneut versuchen',
      ...calendarAuthErrorTranslations.de,
      moveEventTitle: 'Termin verschieben?',
      moveEventDescription: 'Möchten Sie diesen Termin auf die neue Zeit verschieben?',
      moveEventNewTime: 'Neue Zeit',
      deleteEventTitle: 'Termin löschen?',
      deleteEventDescription: 'Möchten Sie diesen Termin löschen?',
      cancel: 'Abbrechen',
      confirmMove: 'Verschieben',
      confirmDelete: 'Löschen'
    }
  })

  const toolbarLabels = computed(() => ({
    viewDay: t('viewDay'),
    viewWeek: t('viewWeek'),
    viewMonth: t('viewMonth'),
    viewYear: t('viewYear'),
    prevDay: t('prevDay'),
    prevWeek: t('prevWeek'),
    prevMonth: t('prevMonthNav'),
    prevYear: t('prevYear'),
    nextDay: t('nextDay'),
    nextWeek: t('nextWeek'),
    nextMonth: t('nextMonthNav'),
    nextYear: t('nextYear')
  }))

  const untitledAppointmentLabel = computed(() => t('addTermin'))

  const eventTypeLabels = computed<CalendarEventTypeLabels>(() => ({
    [CalendarEventTypeEnum.TERMIN]: '',
    [CalendarEventTypeEnum.GERICHTSTERMIN]: t('eventTypeGerichtstermin'),
    [CalendarEventTypeEnum.FRIST]: t('addFrist'),
    [CalendarEventTypeEnum.WIEDERVORLAGE]: t('addWiedervorlage'),
    [CalendarEventTypeEnum.AUFGABE]: t('addAufgabe'),
    [CalendarEventTypeEnum.PRIVAT]: t('eventTypePrivate')
  }))

  const sidebarFilterLabels = computed<CalendarPageFilterLabels>(() => ({
    [CalendarEventTypeEnum.TERMIN]: t('filterGeneralAppointments'),
    [CalendarEventTypeEnum.GERICHTSTERMIN]: t('filterCourtAppointments'),
    [CalendarEventTypeEnum.FRIST]: t('filterDeadlines'),
    [CalendarEventTypeEnum.WIEDERVORLAGE]: t('filterResubmissions'),
    [CalendarEventTypeEnum.AUFGABE]: t('filterTasks'),
    [CalendarEventTypeEnum.PRIVAT]: t('filterPrivate')
  }))

  const {
    calendars: connectedCalendars,
    sidebarVisible,
    miniMonthCells,
    miniMonthLabel,
    selectDate,
    miniMonthPrev,
    miniMonthNext,
    toggleSidebar,
    sidebarItems,
    visibleCalendars,
    hasCalendars,
    expandedCalendarUuids,
    selectedCalendarUuid,
    isLoading: isLoadingCalendars,
    loadError,
    loadCalendars,
    selectCalendar,
    toggleFiltersExpanded,
    selectedDate,
    goToToday,
    activeView,
    views,
    heading,
    prevLabel,
    nextLabel,
    setView,
    goPrev,
    goNext,
    eventsByDate,
    eventDates,
    onYearOpenDay,
    getToday,
    appendEvent,
    removeEvent,
    gridEventsByUuid,
    toggleFilter
  } = useCalendarPage(toolbarLabels, sidebarFilterLabels, untitledAppointmentLabel)

  const userStore = useUserStore()
  const route = useRoute()
  const router = useRouter()
  const { calendarKindChecks, setAccountKindSync } = useCalendarKindChecks(connectedCalendars)
  const composerOpen = ref(false)
  const settingsOpen = ref(false)
  const settingsDisconnectingAccountUuid = ref<string | null>(null)
  const composerMode = ref<CalendarEventTypeEnum>(CalendarEventTypeEnum.TERMIN)
  const composerSaving = ref(false)
  const selectedEventUuid = ref<string | null>(null)
  const editingEventUuid = ref<string | null>(null)
  const pendingMove = ref<{
    eventUuid: string
    start: string
    end: string
    allDay: boolean
  } | null>(null)
  const pendingDeleteEventUuid = ref<string | null>(null)
  const moveSaving = ref(false)
  const deleteSaving = ref(false)

  const defaultComposerCalendarUuid = computed(() => {
    const focusedEditableCalendar = visibleCalendars.value.find(
      (calendar) => calendar.uuid === selectedCalendarUuid.value && !calendar.calendar_readonly
    )
    if (focusedEditableCalendar) {
      return focusedEditableCalendar.uuid ?? ''
    }

    const primaryEditableCalendar = visibleCalendars.value.find(
      (calendar) => calendar.calendar_primary && !calendar.calendar_readonly
    )
    if (primaryEditableCalendar) {
      return primaryEditableCalendar.uuid ?? ''
    }

    return visibleCalendars.value.find((calendar) => !calendar.calendar_readonly)?.uuid ?? ''
  })

  const weekdayLabels = computed(() => [
    t('mon'),
    t('tue'),
    t('wed'),
    t('thu'),
    t('fri'),
    t('sat'),
    t('sun')
  ])

  const shellStyle = {
    '--calendar-sidebar-w': CALENDAR_SIDEBAR_WIDTH
  }

  const extractCalendarErrorMessage = (error: unknown): string | undefined => {
    if (!(error instanceof ApiError) || !error.body || typeof error.body !== 'object') {
      return undefined
    }

    const body = error.body as Record<string, unknown>
    if (typeof body.detail === 'string') {
      return body.detail
    }
    if (typeof body.error === 'string') {
      return body.error
    }

    for (const value of Object.values(body)) {
      if (typeof value === 'string') {
        return value
      }
      if (Array.isArray(value) && typeof value[0] === 'string') {
        return value[0]
      }
    }

    return undefined
  }

  const composerPresetDate = computed(() => {
    const year = selectedDate.value.getFullYear()
    const month = String(selectedDate.value.getMonth() + 1).padStart(2, '0')
    const day = String(selectedDate.value.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })

  const showNoCalendarMain = computed(
    () => !isLoadingCalendars.value && !loadError.value && !hasCalendars.value
  )
  const canDragEvents = computed(() =>
    Boolean(
      visibleCalendars.value.find(
        (calendar) => calendar.uuid === selectedCalendarUuid.value && !calendar.calendar_readonly
      )
    )
  )
  const selectedEvent = computed<CalendarGridEvent | null>(
    () => gridEventsByUuid.value.get(selectedEventUuid.value ?? '') ?? null
  )
  const editingEvent = computed<CalendarGridEvent | null>(
    () => gridEventsByUuid.value.get(editingEventUuid.value ?? '') ?? null
  )
  const selectedEventCaseHref = computed(() => {
    if (!selectedEvent.value?.caseUuid) {
      return null
    }

    return ROUTES.APP.CASES.CASE_DETAIL.ROOT.replace(':uuid', selectedEvent.value.caseUuid)
  })
  const moveDialogOpen = computed({
    get: () => Boolean(pendingMove.value),
    set: (open: boolean) => {
      if (!open) {
        pendingMove.value = null
      }
    }
  })
  const deleteDialogOpen = computed({
    get: () => Boolean(pendingDeleteEventUuid.value),
    set: (open: boolean) => {
      if (!open) {
        pendingDeleteEventUuid.value = null
      }
    }
  })

  const formatMoveRange = (startIso: string, endIso: string, allDay: boolean): string => {
    const start = new Date(startIso)
    const end = new Date(endIso)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return ''
    }

    const dateFmt = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      day: '2-digit',
      month: 'long'
    })
    const timeFmt = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    })

    if (allDay) {
      return `${dateFmt.format(start)} · ${t('allDay')}`
    }

    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()

    if (sameDay) {
      return `${dateFmt.format(start)} · ${timeFmt.format(start)}-${timeFmt.format(end)}`
    }

    return `${dateFmt.format(start)} ${timeFmt.format(start)} → ${dateFmt.format(end)} ${timeFmt.format(end)}`
  }

  const pendingMoveSummary = computed(() => {
    if (!pendingMove.value) {
      return ''
    }

    return formatMoveRange(pendingMove.value.start, pendingMove.value.end, pendingMove.value.allDay)
  })

  const onSelectDate = (d: Date): void => {
    selectDate(d)
  }

  const openComposer = (mode: CalendarEventTypeEnum): void => {
    editingEventUuid.value = null
    composerMode.value = mode
    composerOpen.value = true
  }

  const closeComposer = (): void => {
    composerOpen.value = false
    editingEventUuid.value = null
  }

  const openSettings = (): void => {
    settingsOpen.value = true
  }

  const closeSettings = (): void => {
    settingsOpen.value = false
  }

  const consumeSettingsQuery = async (): Promise<void> => {
    const shouldOpenSettings =
      route.query.calendarSettings === '1' || typeof route.query.calendarAuthError === 'string'
    if (!shouldOpenSettings) {
      return
    }

    settingsOpen.value = true
    const authError = getCalendarAuthErrorMessage(route.query.calendarAuthError, t)
    if (authError) {
      genericErrorToast(authError)
    }
    const nextQuery = { ...route.query }
    delete nextQuery.calendarSettings
    delete nextQuery.calendarAuthError
    await router.replace({ query: nextQuery })
  }

  onMounted(() => {
    void consumeSettingsQuery()
  })

  const openEventDetail = (event: CalendarGridEvent): void => {
    selectedEventUuid.value = event.uuid
  }

  const closeEventDetail = (): void => {
    selectedEventUuid.value = null
  }

  const openEventEdit = (): void => {
    if (!selectedEvent.value?.canEdit) {
      return
    }

    editingEventUuid.value = selectedEvent.value.uuid
    composerMode.value = composerModeForEvent(selectedEvent.value)
    composerOpen.value = true
    selectedEventUuid.value = null
  }

  const requestDeleteEvent = (): void => {
    if (!selectedEvent.value?.canEdit) {
      return
    }

    pendingDeleteEventUuid.value = selectedEvent.value.uuid
  }

  const requestMoveEvent = (value: { eventUuid: string; start: string; end: string }): void => {
    pendingMove.value = {
      ...value,
      allDay: gridEventsByUuid.value.get(value.eventUuid)?.allDay ?? false
    }
  }

  const confirmMoveEvent = async (): Promise<void> => {
    const pending = pendingMove.value
    if (!pending) {
      return
    }

    moveSaving.value = true

    try {
      const event = await CalendarEventsService.calendarEventsPartialUpdate({
        uuid: pending.eventUuid,
        requestBody: {
          start: pending.start,
          end: pending.end
        }
      })

      await appendEvent(event)
      pendingMove.value = null
    } catch (error) {
      console.error('[calendar] confirmMoveEvent failed', error)
      genericErrorToast(extractCalendarErrorMessage(error))
    } finally {
      moveSaving.value = false
    }
  }

  const handleComposerSave = async (requestBody: CalendarEventRequest): Promise<void> => {
    composerSaving.value = true

    try {
      const event = editingEvent.value
        ? await CalendarEventsService.calendarEventsPartialUpdate({
            uuid: editingEvent.value.uuid,
            requestBody
          })
        : await CalendarEventsService.calendarEventsCreate({
            requestBody
          })

      await appendEvent(event)
      composerOpen.value = false
      editingEventUuid.value = null
    } catch (error) {
      console.error('[calendar] handleComposerSave failed', error)
      genericErrorToast(extractCalendarErrorMessage(error))
    } finally {
      composerSaving.value = false
    }
  }

  const confirmDeleteEvent = async (): Promise<void> => {
    const eventUuid = pendingDeleteEventUuid.value
    if (!eventUuid) {
      return
    }

    deleteSaving.value = true

    try {
      await CalendarEventsService.calendarEventsDestroy({
        uuid: eventUuid
      })
      pendingDeleteEventUuid.value = null
      selectedEventUuid.value = null
      editingEventUuid.value = null
      removeEvent(eventUuid)
    } catch (error) {
      console.error('[calendar] confirmDeleteEvent failed', error)
      genericErrorToast(extractCalendarErrorMessage(error))
    } finally {
      deleteSaving.value = false
    }
  }

  const disconnectSettingsAccount = async (accountUuid: string): Promise<void> => {
    settingsDisconnectingAccountUuid.value = accountUuid

    try {
      await AccountsService.accountsDestroy({ uuid: accountUuid })
      await loadCalendars()
    } catch (error) {
      console.error('[calendar] disconnectSettingsAccount failed', error)
      genericErrorToast(extractCalendarErrorMessage(error))
    } finally {
      settingsDisconnectingAccountUuid.value = null
    }
  }

  const onConfirmMoveClick = (event: Event): void => {
    event.preventDefault()
    void confirmMoveEvent()
  }

  const onConfirmDeleteClick = (event: Event): void => {
    event.preventDefault()
    void confirmDeleteEvent()
  }
</script>

<template>
  <div
    class="calendar-page -mx-3 flex h-[calc(100vh-3.75rem)] max-h-[calc(100vh-3.75rem)] flex-col
      overflow-hidden sm:-mx-4 md:-mx-6 lg:-mx-12"
    :style="shellStyle"
    data-cy="calendar-page-shell"
  >
    <div
      class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border border-gray-200 bg-white shadow-sm
        ring-1 ring-gray-900/5"
      :class="sidebarVisible ? 'rounded-tr-xl' : 'rounded-t-xl'"
    >
      <div class="flex h-full min-h-0 min-w-0 flex-1">
        <CalendarSidebar
          v-if="sidebarVisible"
          :mini-month-label="miniMonthLabel"
          :mini-month-cells="miniMonthCells"
          :weekday-labels="weekdayLabels"
          :prev-month-label="t('prevMonth')"
          :next-month-label="t('nextMonth')"
          :calendars-heading="t('calendars')"
          :settings-label="t('settings')"
          :empty-title="t('emptyTitle')"
          :empty-description="t('emptyDescription')"
          :setup-label="t('setupCta')"
          :load-failed-message="t('loadFailed')"
          :retry-label="t('retry')"
          :expand-filters-label="t('expandFilters')"
          :collapse-filters-label="t('collapseFilters')"
          :sidebar-items="sidebarItems"
          :selected-calendar-uuid="selectedCalendarUuid"
          :expanded-calendar-uuids="expandedCalendarUuids"
          :is-loading-calendars="isLoadingCalendars"
          :load-error="loadError"
          @mini-prev="miniMonthPrev"
          @mini-next="miniMonthNext"
          @select-date="selectDate"
          @select-calendar="selectCalendar"
          @toggle-filters="toggleFiltersExpanded"
          @toggle-filter="toggleFilter"
          @open-settings="openSettings"
          @retry="loadCalendars"
        />

        <div class="calendar-main flex min-h-0 min-w-0 flex-1 flex-col">
          <CalendarMainToolbar
            :sidebar-visible="sidebarVisible"
            :hide-sidebar-label="t('hideSidebar')"
            :show-sidebar-label="t('showSidebar')"
            :views="views"
            :active-view="activeView"
            :heading-date="heading.date"
            :heading-year="heading.year"
            :prev-label="prevLabel"
            :next-label="nextLabel"
            :today-label="t('today')"
            :add-event-label="t('addEvent')"
            :add-termin-label="t('addTermin')"
            :add-frist-label="t('addFrist')"
            :add-wiedervorlage-label="t('addWiedervorlage')"
            :add-aufgabe-label="t('addAufgabe')"
            :has-calendars="hasCalendars"
            @toggle-sidebar="toggleSidebar"
            @set-view="setView"
            @go-prev="goPrev"
            @go-next="goNext"
            @go-today="goToToday"
            @add-event="openComposer"
          />

          <div
            class="flex min-h-0 flex-1 flex-col overflow-hidden"
            data-cy="calendar-main-area"
          >
            <CalendarMainEmptyState
              v-if="showNoCalendarMain"
              :title="t('emptyTitle')"
              :description="t('emptyDescription')"
              :setup-label="t('setupCta')"
              :setup-href="APPOINTMENTS_CALENDAR_SETUP_PATH"
            />
            <CalendarMainPanel
              v-else
              :selected-date="selectedDate"
              :active-view="activeView"
              :today="getToday()"
              :weekday-labels="weekdayLabels"
              :events-by-date="eventsByDate"
              :appointment-dates="eventDates"
              :all-day-label="t('allDay')"
              :more-label="t('more')"
              :event-type-labels="eventTypeLabels"
              :can-drag-events="canDragEvents"
              @open-event="openEventDetail"
              @request-move-event="requestMoveEvent"
              @update:selected-date="onSelectDate"
              @open-day-view="onYearOpenDay"
            />
          </div>
        </div>
      </div>
    </div>

    <CalendarEventComposerModal
      :open="composerOpen"
      :mode="composerMode"
      :event="editingEvent"
      :calendars="visibleCalendars"
      :default-calendar-uuid="defaultComposerCalendarUuid"
      :default-owner-uuid="userStore.user?.uuid ?? null"
      :preset-date="composerPresetDate"
      :saving="composerSaving"
      @close="closeComposer"
      @save="handleComposerSave"
    />

    <CalendarSettingsModal
      :open="settingsOpen"
      :calendars="connectedCalendars"
      :calendar-kind-checks="calendarKindChecks"
      :current-user="userStore.user ?? null"
      :disconnecting-account-uuid="settingsDisconnectingAccountUuid"
      :accounts-loading="isLoadingCalendars"
      @close="closeSettings"
      @disconnect-account="disconnectSettingsAccount"
      @set-account-kind-sync="setAccountKindSync"
    />

    <CalendarEventDetailDialog
      :open="Boolean(selectedEvent)"
      :event="selectedEvent"
      :calendars="visibleCalendars"
      :event-type-labels="eventTypeLabels"
      :all-day-label="t('allDay')"
      :case-href="selectedEventCaseHref"
      :can-edit="Boolean(selectedEvent?.canEdit)"
      :deleting="deleteSaving"
      @edit="openEventEdit"
      @delete="requestDeleteEvent"
      @close="closeEventDetail"
    />

    <AlertDialog v-model:open="moveDialogOpen">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('moveEventTitle') }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ t('moveEventDescription') }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div
          v-if="pendingMoveSummary"
          class="border-line-2 bg-surface rounded-lg border px-3 py-2 text-sm text-foreground"
        >
          <span class="text-muted-foreground-1">{{ t('moveEventNewTime') }}:</span>
          {{ pendingMoveSummary }}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>{{ t('cancel') }}</AlertDialogCancel>
          <button
            type="button"
            :disabled="moveSaving"
            class="shadow-xs focus:outline-hidden inline-flex items-center justify-center rounded-md bg-primary px-4
              py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:pointer-events-none
              disabled:opacity-50"
            @click="onConfirmMoveClick"
          >
            {{ moveSaving ? '...' : t('confirmMove') }}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog v-model:open="deleteDialogOpen">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('deleteEventTitle') }}</AlertDialogTitle>
          <AlertDialogDescription>{{ t('deleteEventDescription') }}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{{ t('cancel') }}</AlertDialogCancel>
          <button
            type="button"
            :disabled="deleteSaving"
            class="shadow-xs focus:outline-hidden inline-flex items-center justify-center rounded-md bg-primary px-4
              py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:pointer-events-none
              disabled:opacity-50"
            @click="onConfirmDeleteClick"
          >
            {{ deleteSaving ? '...' : t('confirmDelete') }}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
